import { useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { type AuditTemplateRef, useUpdateAuditTemplates } from "@/hooks/use-audits";
import { useTemplates } from "@/hooks/use-templates";

interface EditAuditTemplatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditId: string;
  currentTemplates: AuditTemplateRef[];
}

export function EditAuditTemplatesDialog({
  open,
  onOpenChange,
  auditId,
  currentTemplates,
}: EditAuditTemplatesDialogProps) {
  const { data: activeTemplates } = useTemplates({ status: "active" });
  const updateTemplates = useUpdateAuditTemplates(auditId);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showZeroError, setShowZeroError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const currentIds = useMemo(() => currentTemplates.map((t) => t.id), [currentTemplates]);
  // Read via a ref (not a dependency) so a background refetch of
  // currentTemplates while the dialog is already open can't stomp the
  // user's in-progress selection — the reset should only happen when the
  // dialog transitions to open.
  const currentIdsRef = useRef(currentIds);
  currentIdsRef.current = currentIds;

  useEffect(() => {
    if (open) {
      setSelectedIds(currentIdsRef.current);
      setShowZeroError(false);
      setConfirmOpen(false);
    }
  }, [open]);

  // A currently-applied template may no longer be "active" (its status
  // changed after this audit was created) — still show it, checked, so
  // editing the set doesn't silently drop it.
  const templateOptions = useMemo(() => {
    const options = [...(activeTemplates ?? [])];
    const knownIds = new Set(options.map((t) => t.id));
    for (const t of currentTemplates) {
      if (!knownIds.has(t.id)) {
        options.push({
          id: t.id,
          name: t.name,
          description: null,
          projectType: "",
          status: "inactive",
          createdById: "",
          createdByName: null,
          createdAt: "",
          updatedAt: "",
        });
      }
    }
    return options;
  }, [activeTemplates, currentTemplates]);

  function toggle(id: string) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) {
          setShowZeroError(true);
          return prev;
        }
        return prev.filter((existingId) => existingId !== id);
      }
      setShowZeroError(false);
      return [...prev, id];
    });
  }

  const removedTemplates = currentTemplates.filter((t) => !selectedIds.includes(t.id));

  function handleSave() {
    if (selectedIds.length === 0) {
      setShowZeroError(true);
      return;
    }
    if (removedTemplates.length > 0) {
      setConfirmOpen(true);
      return;
    }
    updateTemplates.mutate({ templateIds: selectedIds }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Templates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border border-input p-3">
              {templateOptions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active templates</p>
              ) : (
                templateOptions.map((t) => (
                  <div key={t.id} className="flex items-center gap-3">
                    <Checkbox
                      id={`edit-audit-template-${t.id}`}
                      checked={selectedIds.includes(t.id)}
                      onCheckedChange={() => toggle(t.id)}
                    />
                    <Label
                      htmlFor={`edit-audit-template-${t.id}`}
                      className="cursor-pointer font-normal"
                    >
                      {t.name}
                      {t.status !== "active" && (
                        <span className="text-muted-foreground"> (inactive)</span>
                      )}
                    </Label>
                  </div>
                ))
              )}
            </div>
            {showZeroError && (
              <p className="text-sm text-destructive">At least one template is required.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={updateTemplates.isPending}>
                {updateTemplates.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Remove templates?"
        description={`This will permanently delete the items introduced by ${removedTemplates
          .map((t) => `"${t.name}"`)
          .join(
            ", ",
          )} — including any checked-off progress and comments on those items. A section shared with a template that's still applied will keep that template's items. This cannot be undone.`}
        confirmLabel="Remove"
        variant="destructive"
        loading={updateTemplates.isPending}
        onConfirm={async () => {
          await updateTemplates.mutateAsync({ templateIds: selectedIds });
          setConfirmOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
